import { useEffect, memo } from 'react'

import type {
  HcStructureMenuCustomEvent,
  IHcStructureMenuToggleEvent
} from '@hotmart-org-ca/cosmos-web'
import { SessionMode } from '@space-shell/common/components/baseStructure/enum'
import useCustomEvent from '@space-shell/common/hooks/useCustomEvent'

import { useViewAsStore } from '@space-shell/core/store'
import { useParentInfoStore } from '@space-shell/remotes/preview/store/parentInfo'

import { MenuEvents } from './events'

import MenuFooter from './menuFooter'
import MenuItem from './menuItem'
import { HcMenu, menuStyle } from './styles'
import type { MenuProps } from './types'

import { useMenuContext } from '../context/useMenuContext'

const Menu = ({ children, session, color, menuItems, theme }: MenuProps) => {
  const { emitEvent } = useCustomEvent()
  const { menuRef } = useMenuContext()
  const { theme: previewTheme, isEditingColor } = useParentInfoStore()
  const { isEnabled: isViewAsEnabled } = useViewAsStore()

  const isSessionPreviewEnable = session === SessionMode.PREVIEW

  useEffect(() => {
    if (!menuRef.current) {
      return
    }

    const styles = document.createElement('style')
    const shadowRoot = menuRef.current?.shadowRoot

    styles.id = 'custom-menu-styles'
    styles.innerHTML = menuStyle.toString()
    shadowRoot?.prepend(styles)

    return () => {
      styles.remove()
    }
  }, [menuRef])

  useEffect(() => {
    const menuToggleListener = (event: Event) => {
      const { opened } = (event as HcStructureMenuCustomEvent<IHcStructureMenuToggleEvent>).detail
      emitEvent(MenuEvents.TOGGLE_MENU, { isOpen: opened })
    }

    window.addEventListener('toggle', menuToggleListener)

    return () => window.removeEventListener('toggle', menuToggleListener)
  }, [emitEvent])

  return (
    <HcMenu
      overlay
      opened={isEditingColor}
      ref={menuRef}
      slot='menu'
      $theme={isSessionPreviewEnable ? previewTheme : theme}
      $session={session}
      {...(!isSessionPreviewEnable && { $isViewAs: isViewAsEnabled })}>
      {menuItems?.map(({ label, iconPrimary, onClick, isActive, className }, index) => (
        <MenuItem
          key={index}
          session={SessionMode.DEFAULT}
          color={String(color)}
          label={label}
          iconPrimary={iconPrimary}
          onClick={onClick}
          isActive={isActive}
          className={className}
        />
      ))}
      {children}

      <MenuFooter />
    </HcMenu>
  )
}

export default memo(Menu)
